import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faCropSimple, faChevronLeft, faChevronRight, faTrash,
} from '@fortawesome/free-solid-svg-icons';

// Full-image preview with the stored crop box drawn on top (read-only), plus
// prev/next navigation across the wine's image attachments (arrow keys too) and
// a Remove action — built for triaging mis-attached (wrong-wine) images quickly.
// "Edit crop box" hands off to AttachmentCropEditor for the current image.
//
// `items` are the image libationAttachment rows (each has `.attachment`).

const isFullFrame = (b) => b && b.w >= 0.999 && b.h >= 0.999 && (b.x || 0) <= 0.001 && (b.y || 0) <= 0.001;
const hasUsableBox = (b) => b && b.w > 0 && b.h > 0 && !isFullFrame(b);

const AttachmentCropPreview = ({ items, initialIndex, onEdit, onRemove, onClose }) => {
  const [idx, setIdx] = useState(initialIndex || 0);
  const [dims, setDims] = useState(null);

  const count = items.length;
  const safeIdx = Math.min(Math.max(idx, 0), Math.max(count - 1, 0));
  const item = items[safeIdx];
  const att = item && item.attachment;

  // Close if there's nothing left to show (e.g. after removing the last one).
  useEffect(() => {
    if (count === 0) onClose();
  }, [count, onClose]);

  // Size the stage in the untrimmed frame — the same frame the cropBox lives in
  // and the `?w=` display img renders. Stored width/height (phash worker, from the
  // raw S3 object) ARE that frame, so use them when present; otherwise read the
  // NO-PARAM original. Never a `?w=` variant (width-only resize → squashed aspect)
  // or `f=inside` (the CDN trims whitespace → a different, tall aspect than what
  // this stage displays, which would make the box overlay land wrong).
  useEffect(() => {
    if (!att) return undefined;
    if (att.width && att.height) { setDims({ w: att.width, h: att.height }); return undefined; }
    setDims(null);
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = `${process.env.REACT_APP_ATTACHMENT_URL}/${att.path}`;
    return () => { img.onload = null; };
  }, [att && att.id, att && att.path, att && att.width, att && att.height]);

  // Keyboard: ←/→ navigate, Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, count - 1));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, onClose]);

  if (!att) return null;

  const url = `${process.env.REACT_APP_ATTACHMENT_URL}/${att.path}`;
  const box = att.cropBox;
  const usable = hasUsableBox(box);

  const maxW = Math.min(620, (typeof window !== 'undefined' ? window.innerWidth : 800) * 0.9);
  const maxH = (typeof window !== 'undefined' ? window.innerHeight : 900) * 0.72;
  const ready = !!(dims && dims.w && dims.h);
  let dispW = maxW;
  let dispH = maxH;
  if (ready) {
    const scale = Math.min(maxW / dims.w, maxH / dims.h);
    dispW = Math.round(dims.w * scale);
    dispH = Math.round(dims.h * scale);
  }
  const pct = (n) => `${(n * 100).toFixed(2)}%`;
  const statusText = usable ? 'Crop box' : (isFullFrame(box) ? 'Full frame (no crop)' : 'No crop box set');

  const go = (delta) => (e) => { e.stopPropagation(); setIdx((i) => Math.min(Math.max(i + delta, 0), count - 1)); };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center overflow-auto p-4"
      onClick={onClose}
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap justify-center" onClick={(e) => e.stopPropagation()}>
        <span className="text-white/70 text-sm tabular-nums">{safeIdx + 1} / {count}</span>
        <span className="text-white text-sm">{statusText}</span>
        <button
          onClick={() => onEdit(att)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-900 bg-white rounded-md hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faCropSimple} />
          Edit crop box
        </button>
        {onRemove && (
          <button
            onClick={() => onRemove(item)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600/90 rounded-md hover:bg-red-600"
            title="Remove this image from the wine"
          >
            <FontAwesomeIcon icon={faTrash} />
            Remove
          </button>
        )}
        <a
          href={`${url}?f=inside`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-white text-sm hover:text-gray-300 underline"
        >
          Open full image
        </a>
        <button onClick={onClose} className="text-white hover:text-gray-300">
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </button>
      </div>

      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={go(-1)}
          disabled={safeIdx === 0}
          className="text-white/80 hover:text-white disabled:opacity-30 p-2"
          aria-label="Previous"
        >
          <FontAwesomeIcon icon={faChevronLeft} size="2x" />
        </button>

        {ready ? (
          <div
            className="relative bg-gray-900 rounded overflow-hidden shadow-lg"
            style={{ width: dispW, height: dispH }}
          >
            <img
              src={`${url}?w=${Math.min(1200, dispW * 2)}`}
              alt=""
              // Stored dims are occasionally wrong for pipeline-imported images
              // (a tall aspect recorded for a normal bottle → the stage stretches
              // under objectFit:fill). The actually-served image is the source of
              // truth, so when its aspect disagrees with `dims` (>2%), adopt it —
              // correct images are untouched.
              onLoad={(e) => {
                const w = e.target.naturalWidth;
                const h = e.target.naturalHeight;
                if (!w || !h) return;
                setDims((prev) => {
                  if (!prev || !prev.w || !prev.h) return { w, h };
                  const disagree = Math.abs(h / w - prev.h / prev.w) / (prev.h / prev.w) > 0.02;
                  return disagree ? { w, h } : prev;
                });
              }}
              style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
            />
            {usable && (
              <div
                style={{
                  position: 'absolute',
                  left: pct(box.x),
                  top: pct(box.y),
                  width: pct(box.w),
                  height: pct(box.h),
                  border: '2px solid #fff',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                }}
              />
            )}
          </div>
        ) : (
          <div className="text-white text-sm py-10" style={{ width: maxW, textAlign: 'center' }}>Loading…</div>
        )}

        <button
          onClick={go(1)}
          disabled={safeIdx === count - 1}
          className="text-white/80 hover:text-white disabled:opacity-30 p-2"
          aria-label="Next"
        >
          <FontAwesomeIcon icon={faChevronRight} size="2x" />
        </button>
      </div>
    </div>
  );
};

export default AttachmentCropPreview;
